import type { CityConfig } from "../hawaii-city/types";

export const honoluluConfig: CityConfig = {
  city: "Honolulu",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "honolulu",
  route: "/cities/honolulu",
  title: "Emergency & Expedited Passport Services in Honolulu, HI — Same-Day Processing | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Honolulu, HI — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Oahu and all Hawaiian Islands travelers.",
  h1: "Emergency & Expedited Passport Services in Honolulu, HI",
  heroSubtitle: "Passport expired? Flying soon? We help Honolulu and Oahu travelers get their passports fast. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20Honolulu%20Hawaii%20skyline%20at%20golden%20hour%2C%20Waikiki%20Beach%20in%20foreground%20with%20turquoise%20ocean%2C%20Diamond%20Head%20crater%20visible%20in%20background%2C%20modern%20downtown%20buildings%20against%20dramatic%20tropical%20sky%2C%20warm%20sunset%20lighting%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%20with%20deep%20blue%20ocean%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=honolulu-hero-01&orientation=landscape",
  heroImageAlt: "Honolulu Hawaii skyline at golden hour with Waikiki Beach and Diamond Head",
  geoPosition: "21.3099;-157.8581",
  geoPlacename: "Honolulu, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "Located in downtown Honolulu",
  },
  nearbyCitiesZips: [
    { name: "Honolulu", zips: "96815" },
    { name: "Waikiki", zips: "96815" },
    { name: "Pearl City", zips: "96782" },
    { name: "Kailua", zips: "96734" },
    { name: "Kaneohe", zips: "96744" },
    { name: "Aiea", zips: "96701" },
    { name: "Waimalu", zips: "96701" },
    { name: "Mililani", zips: "96789" },
    { name: "Ewa Beach", zips: "96706" },
    { name: "Kapolei", zips: "96707" },
    { name: "Wahiawa", zips: "96786" },
    { name: "Haleiwa", zips: "96712" },
    { name: "Laie", zips: "96762" },
    { name: "Kahuku", zips: "96731" },
    { name: "Kaaawa", zips: "96730" },
    { name: "Punaluu", zips: "96717" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping to send your documents directly to us — we handle the rest." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed." },
  ],
  testimonials: [
    {
      name: "Kai M.",
      location: "Honolulu, HI",
      rating: 5,
      text: "I had a business trip to Tokyo in 4 days and my passport was expired. Fast Passport Center processed my renewal in 3 days. Their team was incredibly responsive and kept me updated every step of the way.",
      service: "Passport Renewal",
    },
    {
      name: "Leilani K.",
      location: "Waikiki, HI",
      rating: 5,
      text: "Our family cruise to the South Pacific was in 2 weeks and my son's passport was nowhere to be found. They walked us through the replacement process and had a new passport in his hands with time to spare.",
      service: "Lost Passport Replacement",
    },
    {
      name: "David T.",
      location: "Kailua, HI",
      rating: 5,
      text: "Emergency trip to Manila for a family matter. Called FPC from Kailua, started online the same day, shipped overnight, and had my passport in under a week. I honestly didn't think it was possible from Hawaii.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "honolulu-usps-main",
      name: "Honolulu Main Post Office",
      type: "USPS Passport Acceptance",
      address: "3350 Manoa Rd",
      city: "Honolulu",
      state: "HI",
      zip: "96822",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Manoa, Honolulu",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "honolulu-usps-downtown",
      name: "Downtown Honolulu Post Office",
      type: "USPS Passport Acceptance",
      address: "3600 Waialae Ave",
      city: "Honolulu",
      state: "HI",
      zip: "96816",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm, Sat 9am–12pm",
      distance: "Kaimuki, Honolulu",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Popular for downtown Honolulu residents. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "honolulu-usps-airport",
      name: "Honolulu Airport Post Office",
      type: "USPS Passport Acceptance",
      address: "300 Rodgers Blvd",
      city: "Honolulu",
      state: "HI",
      zip: "96819",
      phone: "800-275-8777",
      hours: "Mon–Fri 8am–4pm",
      distance: "Honolulu Airport area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Convenient for airport area residents. Call ahead to confirm services.",
      icon: "ri-mail-line",
    },
    {
      id: "honolulu-kaneohe",
      name: "Kaneohe Post Office",
      type: "USPS Passport Acceptance",
      address: "45-690 Kamehameha Hwy",
      city: "Kaneohe",
      state: "HI",
      zip: "96744",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Windward Oahu — about 12 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Windward Oahu option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "honolulu-kailua",
      name: "Kailua Post Office",
      type: "USPS Passport Acceptance",
      address: "130 Kailua Rd",
      city: "Kailua",
      state: "HI",
      zip: "96734",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Windward Oahu — about 15 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Kailua area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "honolulu-pearl-city",
      name: "Pearl City Post Office",
      type: "USPS Passport Acceptance",
      address: "550 Kamehameha Hwy",
      city: "Pearl City",
      state: "HI",
      zip: "96782",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Leeward Oahu — about 10 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Pearl City area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Honolulu, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours depending on your eligibility and the processing option you select. Standard government processing through a post office or acceptance clerk in Honolulu can take 2–6 months, especially during busy travel seasons.",
    },
    {
      q: "Where is the closest passport agency to Honolulu?",
      a: "The Honolulu Passport Agency is located at 300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850 — in downtown Honolulu. Regional agencies require appointments and are primarily reserved for life-or-death emergencies. Fast Passport Center can often provide a faster, more convenient option using approved courier submission — no appointment required.",
    },
    {
      q: "Do I need to visit an office to use Fast Passport Center?",
      a: "No — most Honolulu travelers start online and ship their documents via overnight courier. There is no local Fast Passport Center office in Honolulu, but that's the point: you don't need one. Start your order online, follow the checklist, and ship your documents overnight from anywhere in Honolulu or Oahu.",
    },
    {
      q: "What documents do I need for an expedited passport?",
      a: "Most applicants need: proof of U.S. citizenship (current or expired U.S. passport, birth certificate, or naturalization certificate), a government-issued photo ID, completed passport forms, and passport photos. We provide a personalized checklist after you start your order.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants typically require an in-person acceptance clerk visit before documents can be submitted for expedited processing. We guide you through exactly what to do and where to go in the Honolulu area.",
    },
    {
      q: "How long does a passport take through the post office in Honolulu?",
      a: "Standard government processing through a Honolulu acceptance facility (post office or clerk) can take 2–6 months depending on government workload and the time of year. If your travel date is sooner than that, using an approved passport courier like Fast Passport Center is the safer option.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We are not a government agency. We work within the official passport system to provide expedited processing through reserved courier submission availability.",
    },
    {
      q: "What if I need a child passport in Honolulu?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions and a checklist so you know exactly what to do at a Honolulu acceptance facility. Expedited processing options are available after the acceptance step.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64 to report the lost passport). We guide you through exactly what's needed and help ensure the correct process is followed to avoid delays.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — S King St", address: "1488 S King St, Honolulu, HI 96814", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Kapiolani Blvd", address: "1620 Kapiolani Blvd, Honolulu, HI 96814", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Keeaumoku St", address: "2222 Kuhio Ave, Honolulu, HI 96815", hours: "Daily 7 AM – 10 PM" },
    { name: "Walmart — Keeaumoku St", address: "700 Keeaumoku St, Honolulu, HI 96814", hours: "Call for hours" },
    { name: "The UPS Store — Waikiki", address: "2255 Kuhio Ave, Honolulu, HI 96815", hours: "Call for hours" },
    { name: "FedEx Office — Honolulu", address: "1000 Bishop St, Honolulu, HI 96813", hours: "Call for hours" },
    { name: "Walgreens — Kapahulu Ave", address: "4361 Kapahulu Ave, Honolulu, HI 96816", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — N School St", address: "1620 N School St, Honolulu, HI 96817", hours: "Daily 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Honolulu DMV — Downtown Satellite City Hall",
      address: "650 S King St, Honolulu, HI 96813",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kapalama DMV — Dillingham Blvd",
      address: "1188 Dillingham Blvd, Honolulu, HI 96817",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Kalihi area option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "Primary airport — Honolulu International",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub for Hawaii with flights to Asia, Australia, North America, and domestic destinations.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: true,
    },
    {
      code: "OGG",
      name: "Kahului Airport (Maui)",
      distance: "Inter-island flights — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Major inter-island airport serving Maui with connections to Honolulu and international destinations.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel. We prioritize your application and submit directly via reserved agency slots.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Honolulu travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide you through every step of the process.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes and delays.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions and checklists.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports and data corrections.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a significantly damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Honolulu travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Honolulu residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Honolulu residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Honolulu residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Honolulu, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for Honolulu residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Honolulu travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Honolulu travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Honolulu clients ship from home — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to travel or wait at a passport agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–3", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — downtown Honolulu location", fpc: "Most travelers ship documents overnight", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Island Guide", href: "#island-guide" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Kailua", route: "/cities/kailua" },
    { name: "Kaneohe", route: "/cities/kaneohe" },
    { name: "Pearl City", route: "/cities/pearl-city" },
    { name: "Hilo", route: "/cities/hilo" },
    { name: "Waikiki", route: "/cities/waikiki" },
    { name: "Kapolei", route: "/cities/kapolei" },
    { name: "Mililani", route: "/cities/mililani" },
    { name: "Wahiawa", route: "/cities/wahiawa" },
  ],
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your home or office in most cases, ship with our prepaid label from any UPS/FedEx location, or schedule a drop-off. Most clients ship overnight — no visit required.",
  fpcOfficeDistance: "Serving Honolulu and all of Oahu remotely",
  serviceAreaIntro: "Whether you are in Honolulu, Waikiki, Pearl City, or anywhere on Oahu, Fast Passport Center makes expedited passport processing simple — no agency lines, no guessing.",
  nearbyCitiesIntro: "Anywhere on Oahu — start online, ship overnight, and we handle the rest. No need to travel off-island.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Honolulu as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Honolulu travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Honolulu, Waikiki, and Kailua who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Honolulu and Oahu travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Oahu",
  islandNickname: "The Gathering Place",
  landmarkCards: [
    {
      name: "Diamond Head",
      description: "Honolulu's most iconic landmark, this ancient volcanic crater offers panoramic views of Waikiki and the Pacific. A must-visit before any international trip.",
      imageUrl: "https://readdy.ai/api/search-image?query=Diamond%20Head%20crater%20Honolulu%20Hawaii%20viewed%20from%20above%2C%20iconic%20volcanic%20tuff%20cone%20with%20hiking%20trail%2C%20turquoise%20ocean%20and%20Waikiki%20coastline%20in%20background%2C%20warm%20tropical%20sunlight%2C%20lush%20green%20vegetation%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=honolulu-diamond-head&orientation=landscape",
    },
    {
      name: "Waikiki Beach",
      description: "World-famous beach in the heart of Honolulu. Many travelers realize they need passport renewals while planning trips from this iconic shoreline.",
      imageUrl: "https://readdy.ai/api/search-image?query=Waikiki%20Beach%20Honolulu%20Hawaii%20at%20golden%20hour%2C%20palm%20trees%20along%20white%20sand%20shoreline%2C%20turquoise%20Pacific%20Ocean%20with%20gentle%20waves%2C%20Diamond%20Head%20visible%20in%20distance%2C%20warm%20sunset%20lighting%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=honolulu-waikiki&orientation=landscape",
    },
    {
      name: "Iolani Palace",
      description: "The only royal palace on U.S. soil, located in downtown Honolulu. A symbol of Hawaii's unique history and cultural heritage.",
      imageUrl: "https://readdy.ai/api/search-image?query=Iolani%20Palace%20Honolulu%20Hawaii%2C%20historic%20royal%20palace%20with%20white%20columns%20and%20coral%20walls%2C%20lush%20tropical%20gardens%20in%20foreground%2C%20blue%20sky%20with%20white%20clouds%2C%20warm%20afternoon%20sunlight%2C%20professional%20architectural%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=honolulu-iolani&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-flight-takeoff-line", title: "HNL is Your Gateway", description: "Daniel K. Inouye International Airport (HNL) is Oahu's main hub. Most international flights depart from here, so confirm your passport validity before booking." },
    { icon: "ri-ship-line", title: "Cruise Season Timing", description: "Honolulu cruise departures to the South Pacific and Asia peak in winter. Renew your passport at least 3 months before your cruise date." },
    { icon: "ri-calendar-event-line", title: "Japanese Travel Season", description: "Many Honolulu residents travel to Japan for business and family visits. Golden Week (April–May) and Obon (August) are peak times — plan passport renewals early." },
    { icon: "ri-truck-line", title: "Oahu Shipping Advantage", description: "As the state's logistics hub, overnight shipping from Honolulu is the fastest in Hawaii. Our processing center receives Oahu documents next-day." },
    { icon: "ri-map-pin-line", title: "Downtown Document Access", description: "Honolulu's dense business district means easy access to photo services, notary publics, and acceptance clerks — all within a few blocks of each other." },
    { icon: "ri-user-location-line", title: "Military & Government Travel", description: "Joint Base Pearl Harbor-Hickam and Camp Smith are nearby. We frequently process urgent passports for military personnel with TDY and PCS orders." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
};